import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import SecurityMonitor from "@/components/SecurityMonitor";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Calculator from "./pages/Calculator";
import IFTAReports from "./pages/IFTAReports";
import MileageTracker from "./pages/MileageTracker";
import ReceiptScan from "./pages/ReceiptScan";
import VehicleManagement from "./pages/VehicleManagement";
import TripManagement from "./pages/TripManagement";
import Reports from "./pages/Reports";
import Invoices from "./pages/Invoices";
import SiteTest from "./pages/SiteTest";
import Pricing from "./pages/Pricing";
import Account from "./pages/Account";
import Demo from "./pages/Demo";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import Admin from "./pages/Admin";
import BOLManagement from "./pages/BOLManagement";
import PaymentSuccess from "./pages/PaymentSuccess";
import StoreListings from "./components/StoreListings";
import AudienceTargetedLanding from "./components/AudienceTargetedLanding";
import StorePreview from "./components/StorePreview";
import MarketingHub from "./components/MarketingHub";
import TruckingNews from "./pages/TruckingNews";
import PrivacySummary from "./pages/PrivacySummary";

const queryClient = new QueryClient();

const App: React.FC = () => {
  console.log('App rendering...');
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/ifta-reports" element={
              <ProtectedRoute>
                <IFTAReports />
              </ProtectedRoute>
            } />
              <Route path="/mileage-tracker" element={
                <ProtectedRoute>
                  <MileageTracker />
                </ProtectedRoute>
              } />
              <Route path="/vehicles" element={
                <ProtectedRoute>
                  <VehicleManagement />
                </ProtectedRoute>
              } />
              <Route path="/trips" element={
                <ProtectedRoute>
                  <TripManagement />
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              } />
            <Route path="/scan-receipt" element={
              <ProtectedRoute>
                <ReceiptScan />
              </ProtectedRoute>
            } />
            <Route path="/invoices" element={
              <ProtectedRoute>
                <Invoices />
              </ProtectedRoute>
            } />
            <Route path="/calculator" element={<Calculator />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/account" element={<Account />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/site-test" element={<SiteTest />} />
            <Route path="/bol-management" element={
              <ProtectedRoute>
                <BOLManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            } />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/store-listings" element={<StoreListings />} />
            <Route path="/audience-landing" element={<AudienceTargetedLanding />} />
            <Route path="/store-preview" element={<StorePreview />} />
            <Route path="/marketing-hub" element={<MarketingHub />} />
            <Route path="/trucking-news" element={
              <ProtectedRoute>
                <TruckingNews />
              </ProtectedRoute>
            } />
            <Route path="/privacy-summary" element={<PrivacySummary />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
              <SecurityMonitor />
            </TooltipProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
);
};

export default App;
